import {
	DoubleLeftOutlined,
	DoubleRightOutlined,
	LeftOutlined,
	RightOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import type React from "react";
import { useMemo, useState } from "react";
import type { EventOccurrence } from "../../types";
import {
	calculateWeekNavigation,
	formatTime,
	generateTimeSlots,
	getDayHeaderColor,
	getDayOfWeekLabel,
	getJSTHours,
	getJSTMinutes,
	getMonthYearDisplay,
	getStartOfCurrentWeek,
	getWeekDates,
	getWeekRangeDisplay,
	isPastDay,
	isSameDayJST,
	isToday,
} from "../../utils";

interface ScheduleCalendarProps {
	occurrences: EventOccurrence[];
	slotDuration?: number;
	startHour?: number;
	endHour?: number;
	primaryColor?: string;
	primaryLightColor?: string;
	onSlotClick?: (occurrence: EventOccurrence) => void;
	onEmptySlotClick?: (date: Date, startTime: string, endTime: string) => void;
}

export const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
	occurrences,
	slotDuration = 60,
	startHour,
	endHour,
	primaryColor = "#99CA29",
	primaryLightColor = "#fdfff0",
	onSlotClick,
	onEmptySlotClick,
}) => {
	const [currentWeekStart, setCurrentWeekStart] = useState(
		getStartOfCurrentWeek,
	);

	const weekDates = useMemo(
		() => getWeekDates(currentWeekStart),
		[currentWeekStart],
	);

	const effectiveTimeRange = useMemo(() => {
		if (startHour !== undefined && endHour !== undefined) {
			return { startHour, endHour };
		}

		let minHour = 7;
		let maxHour = 21;

		if (occurrences.length > 0) {
			for (const occ of occurrences) {
				if (occ.startAt) {
					const startDate = new Date(occ.startAt);
					if (!Number.isNaN(startDate.getTime())) {
						const hour = getJSTHours(occ.startAt);
						minHour = Math.min(minHour, hour);
					}
				}
				if (occ.endAt) {
					const endDate = new Date(occ.endAt);
					if (!Number.isNaN(endDate.getTime())) {
						const hour =
							getJSTHours(occ.endAt) + (getJSTMinutes(occ.endAt) > 0 ? 1 : 0);
						maxHour = Math.max(maxHour, hour);
					}
				}
			}
		}

		return {
			startHour: Math.max(0, minHour),
			endHour: Math.min(24, maxHour),
		};
	}, [startHour, endHour, occurrences]);

	const timeSlots = useMemo(
		() =>
			generateTimeSlots(
				effectiveTimeRange.startHour,
				effectiveTimeRange.endHour,
				slotDuration,
			),
		[effectiveTimeRange.startHour, effectiveTimeRange.endHour, slotDuration],
	);

	const getOccurrencesForSlot = (
		date: Date,
		slotTime: string,
	): EventOccurrence[] => {
		const [slotHour, slotMinute] = slotTime.split(":").map(Number);
		const slotStartMinutes = slotHour * 60 + slotMinute;
		const slotEndMinutes = slotStartMinutes + slotDuration;

		return occurrences.filter((occ) => {
			if (!occ.startAt || !occ.endAt) {
				return false;
			}

			const occStartDate = new Date(occ.startAt);
			const occEndDate = new Date(occ.endAt);

			if (
				Number.isNaN(occStartDate.getTime()) ||
				Number.isNaN(occEndDate.getTime())
			) {
				return false;
			}

			if (!isSameDayJST(occ.startAt, date)) {
				return false;
			}

			const occStartMinutes =
				getJSTHours(occ.startAt) * 60 + getJSTMinutes(occ.startAt);
			const occEndMinutes =
				getJSTHours(occ.endAt) * 60 + getJSTMinutes(occ.endAt);

			return (
				occStartMinutes < slotEndMinutes && occEndMinutes > slotStartMinutes
			);
		});
	};

	const navigateWeek = (
		direction: "prev" | "next" | "prevMonth" | "nextMonth",
	) => {
		setCurrentWeekStart(calculateWeekNavigation(currentWeekStart, direction));
	};

	const goToToday = () => {
		setCurrentWeekStart(getStartOfCurrentWeek());
	};

	const getSlotBgColor = (
		hasOccurrences: boolean,
		isCurrentDay: boolean,
		isPast: boolean,
	): string => {
		if (hasOccurrences) return "#c9d6a3";
		if (isCurrentDay) return primaryLightColor;
		if (isPast) return "rgba(195, 195, 195, 0.15)";
		return "white";
	};

	return (
		<div>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: "16px",
				}}
			>
				<div
					style={{
						border: `1px solid ${primaryColor}`,
						color: primaryColor,
						borderRadius: "4px",
						padding: "4px 12px",
					}}
				>
					{getWeekRangeDisplay(weekDates)}
				</div>
				<div style={{ display: "flex", gap: "4px" }}>
					<Button
						icon={<DoubleLeftOutlined />}
						onClick={() => navigateWeek("prevMonth")}
					/>
					<Button
						icon={<LeftOutlined />}
						onClick={() => navigateWeek("prev")}
					/>
					<Button onClick={goToToday}>今週</Button>
					<Button
						icon={<RightOutlined />}
						onClick={() => navigateWeek("next")}
					/>
					<Button
						icon={<DoubleRightOutlined />}
						onClick={() => navigateWeek("nextMonth")}
					/>
				</div>
			</div>

			<div
				style={{
					textAlign: "center",
					fontSize: "20px",
					fontWeight: "bold",
					marginBottom: "16px",
				}}
			>
				{getMonthYearDisplay(weekDates)}
			</div>

			<div
				style={{
					display: "grid",
					gridTemplateColumns: "60px repeat(7, 1fr)",
					border: "1px solid #e5e7eb",
					borderRadius: "4px",
					overflow: "hidden",
				}}
			>
				<div
					style={{
						backgroundColor: "#f9fafb",
						borderBottom: "1px solid #e5e7eb",
						borderRight: "1px solid #e5e7eb",
						padding: "8px",
					}}
				/>
				{weekDates.map((date) => (
					<div
						key={date.toISOString()}
						style={{
							backgroundColor: "#f9fafb",
							borderBottom: "1px solid #e5e7eb",
							borderRight: "1px solid #e5e7eb",
							padding: "8px",
							textAlign: "center",
							fontWeight: "bold",
							color: getDayHeaderColor(date),
						}}
					>
						{date.getDate()} ({getDayOfWeekLabel(date)})
					</div>
				))}

				{timeSlots.flatMap((slot) => [
					<div
						key={slot}
						style={{
							backgroundColor: "#f9fafb",
							borderBottom: "1px solid #e5e7eb",
							borderRight: "1px solid #e5e7eb",
							padding: "12px 8px",
							fontSize: "12px",
							textAlign: "right",
						}}
					>
						{slot}
					</div>,
					...weekDates.map((date) => {
						const slotOccurrences = getOccurrencesForSlot(date, slot);
						const hasOccurrences = slotOccurrences.length > 0;
						const isCurrentDay = isToday(date);
						const isPast = isPastDay(date);

						const totalExpected = slotOccurrences.reduce(
							(sum, o) => sum + o.sumExpected,
							0,
						);
						const totalMax = slotOccurrences.reduce(
							(sum, o) => sum + o.maxAttendees,
							0,
						);
						const totalAttended = slotOccurrences.reduce(
							(sum, o) => sum + o.sumAttended,
							0,
						);

						const isInteractive =
							hasOccurrences || (!isPast && onEmptySlotClick);
						const handleClick = () => {
							if (hasOccurrences && onSlotClick) {
								onSlotClick(slotOccurrences[0]);
							} else if (!isPast && onEmptySlotClick) {
								const [hour, minute] = slot.split(":").map(Number);
								const endMinute = minute + slotDuration;
								const endHourCalc = hour + Math.floor(endMinute / 60);
								const endMinuteCalc = endMinute % 60;
								onEmptySlotClick(
									date,
									slot,
									formatTime(endHourCalc, endMinuteCalc),
								);
							}
						};
						const handleKeyDown = (e: React.KeyboardEvent) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								handleClick();
							}
						};
						return (
							// biome-ignore lint/a11y/noStaticElementInteractions: Role is set conditionally based on interactivity
							<div
								key={`${date.toISOString()}-${slot}`}
								role={isInteractive ? "button" : "presentation"}
								tabIndex={isInteractive ? 0 : undefined}
								style={{
									borderBottom: "1px solid #e5e7eb",
									borderRight: "1px solid #e5e7eb",
									padding: "4px",
									minHeight: "70px",
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
									cursor:
										hasOccurrences || (!isPast && onEmptySlotClick)
											? "pointer"
											: "default",
									backgroundColor: getSlotBgColor(
										hasOccurrences,
										isCurrentDay,
										isPast,
									),
								}}
								onClick={isInteractive ? handleClick : undefined}
								onKeyDown={isInteractive ? handleKeyDown : undefined}
							>
								{hasOccurrences && (
									<div
										style={{
											display: "flex",
											gap: "8px",
											width: "100%",
											justifyContent: "center",
										}}
									>
										<div style={{ textAlign: "center" }}>
											<div style={{ fontSize: "11px", color: "#6b7280" }}>
												予約数
											</div>
											<div style={{ fontWeight: "bold", fontSize: "13px" }}>
												{totalExpected}
												<span
													style={{
														color: "#6b7280",
														fontWeight: "normal",
														fontSize: "11px",
													}}
												>
													/{totalMax}
												</span>
											</div>
										</div>
										<div style={{ textAlign: "center" }}>
											<div style={{ fontSize: "11px", color: "#6b7280" }}>
												参加数
											</div>
											<div style={{ fontWeight: "bold", fontSize: "13px" }}>
												{totalAttended}
											</div>
										</div>
									</div>
								)}
							</div>
						);
					}),
				])}
			</div>
		</div>
	);
};
